/**
 * Onboarding Tour Component
 *
 * Welcome flow for first-time users.
 * Shows app philosophy and key features in a step-by-step modal.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Sparkles,
  CheckCircle,
  ScrollText,
  BarChart3,
  Rocket,
  ChevronLeft,
  ChevronRight,
  X,
} from 'lucide-react';
import {
  ONBOARDING_STEPS,
  ONBOARDING_STORAGE_KEY,
  type OnboardingStep,
} from '@have-done/shared/constants';

// Map icon names to Lucide components
const ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  sparkles: Sparkles,
  'check-circle': CheckCircle,
  'scroll-text': ScrollText,
  'bar-chart-3': BarChart3,
  rocket: Rocket,
};

// Highlight colors for different sections
const HIGHLIGHT_COLORS: Record<string, string> = {
  today: 'bg-primary/10 text-primary',
  timeline: 'bg-category-creative/10 text-category-creative',
  summary: 'bg-category-learning/10 text-category-learning',
};

interface OnboardingTourProps {
  onComplete: () => void;
}

export function OnboardingTour({ onComplete }: OnboardingTourProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [isExiting, setIsExiting] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const nextButtonRef = useRef<HTMLButtonElement>(null);

  const step = ONBOARDING_STEPS[currentStep] as OnboardingStep;
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === ONBOARDING_STEPS.length - 1;
  const Icon = ICONS[step.icon] || Sparkles;

  const handleComplete = useCallback(() => {
    setIsExiting(true);
    localStorage.setItem(ONBOARDING_STORAGE_KEY, 'true');
    setTimeout(() => {
      onComplete();
    }, 200);
  }, [onComplete]);

  const handleNext = useCallback(() => {
    if (isLastStep) {
      handleComplete();
    } else {
      setCurrentStep((prev) => prev + 1);
    }
  }, [isLastStep, handleComplete]);

  const handlePrev = useCallback(() => {
    if (!isFirstStep) {
      setCurrentStep((prev) => prev - 1);
    }
  }, [isFirstStep]);

  const handleSkip = useCallback(() => {
    handleComplete();
  }, [handleComplete]);

  // Focus management
  useEffect(() => {
    nextButtonRef.current?.focus();
  }, [currentStep]);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleComplete();
      } else if (e.key === 'ArrowRight' || e.key === 'Enter') {
        handleNext();
      } else if (e.key === 'ArrowLeft' && !isFirstStep) {
        handlePrev();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleComplete, handleNext, handlePrev, isFirstStep]);

  // Trap focus within modal
  useEffect(() => {
    const modal = modalRef.current;
    if (!modal) return;

    const focusableElements = modal.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    const handleTabKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement?.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement?.focus();
        }
      }
    };

    document.addEventListener('keydown', handleTabKey);
    return () => document.removeEventListener('keydown', handleTabKey);
  }, []);

  return (
    <div
      className={`
        fixed inset-0 z-50 flex items-center justify-center p-4
        transition-opacity duration-200
        ${isExiting ? 'opacity-0' : 'opacity-100'}
      `}
      role="dialog"
      aria-modal="true"
      aria-labelledby="onboarding-title"
      aria-describedby="onboarding-description"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        ref={modalRef}
        className={`
          relative w-full max-w-md rounded-2xl bg-bg-primary p-8 shadow-warm-lg
          transform transition-all duration-200
          ${isExiting ? 'scale-95 opacity-0' : 'scale-100 opacity-100'}
        `}
      >
        {/* Skip button */}
        {!isLastStep && (
          <button
            type="button"
            onClick={handleSkip}
            aria-label="Skip onboarding"
            className="
              absolute top-4 right-4 p-1.5 rounded-lg
              text-text-tertiary hover:text-text-primary hover:bg-bg-secondary
              transition-colors
              focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary
            "
          >
            <X className="h-5 w-5" />
          </button>
        )}

        {/* Icon */}
        <div
          className={`
            mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-6
            ${step.highlight ? HIGHLIGHT_COLORS[step.highlight] : 'bg-accent/10 text-accent'}
          `}
        >
          <Icon className="h-8 w-8" aria-hidden="true" />
        </div>

        {/* Content */}
        <h2
          id="onboarding-title"
          className="text-xl font-semibold text-text-primary text-center mb-3"
        >
          {step.title}
        </h2>
        <p
          id="onboarding-description"
          className="text-text-secondary text-center mb-8 leading-relaxed"
        >
          {step.description}
        </p>

        {/* Step indicators */}
        <div className="flex justify-center gap-2 mb-6" role="tablist" aria-label="Onboarding steps">
          {ONBOARDING_STEPS.map((_, index) => (
            <button
              key={index}
              type="button"
              onClick={() => setCurrentStep(index)}
              role="tab"
              aria-selected={index === currentStep}
              aria-label={`Step ${index + 1} of ${ONBOARDING_STEPS.length}`}
              className={`
                w-2.5 h-2.5 rounded-full transition-all duration-200
                focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2
                ${
                  index === currentStep
                    ? 'bg-primary w-6'
                    : 'bg-border-light hover:bg-border-DEFAULT'
                }
              `}
            />
          ))}
        </div>

        {/* Navigation */}
        <div className="flex gap-3">
          {!isFirstStep && (
            <button
              type="button"
              onClick={handlePrev}
              className="
                flex items-center justify-center gap-1 px-4 py-2.5 rounded-lg
                text-sm font-medium text-text-secondary
                bg-bg-secondary hover:bg-bg-tertiary
                transition-colors
                focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary
              "
            >
              <ChevronLeft className="h-4 w-4" aria-hidden="true" />
              Back
            </button>
          )}
          <button
            ref={nextButtonRef}
            type="button"
            onClick={handleNext}
            className={`
              flex-1 flex items-center justify-center gap-1 px-6 py-2.5 rounded-lg
              text-sm font-medium text-white
              bg-primary hover:bg-primary-dark
              transition-colors
              focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2
            `}
          >
            {isLastStep ? (
              'Get Started'
            ) : (
              <>
                Next
                <ChevronRight className="h-4 w-4" aria-hidden="true" />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Hook to check if onboarding has been completed
 */
export function useOnboardingStatus() {
  const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState<boolean | null>(null);

  useEffect(() => {
    const completed = localStorage.getItem(ONBOARDING_STORAGE_KEY) === 'true';
    setHasCompletedOnboarding(completed);
  }, []);

  const completeOnboarding = useCallback(() => {
    localStorage.setItem(ONBOARDING_STORAGE_KEY, 'true');
    setHasCompletedOnboarding(true);
  }, []);

  const resetOnboarding = useCallback(() => {
    localStorage.removeItem(ONBOARDING_STORAGE_KEY);
    setHasCompletedOnboarding(false);
  }, []);

  return {
    hasCompletedOnboarding,
    completeOnboarding,
    resetOnboarding,
    isLoading: hasCompletedOnboarding === null,
  };
}
